import { useTranslation } from "react-i18next";
import { Text } from "theme-ui";
import { FormikConfig, FormikErrors, Formik, FormikProps } from "formik";
import { omitBy, isUndefined } from "lodash";
import type { JSX } from "react";
import { ProfileFormInput, ProfileFormSelect, SubmitButton } from "./ProfileFormInputs";
import {
  FormValues,
  PrimaryInfoFormValues,
  primaryInfoFormKeys,
  sliceProfileValues,
} from "./support";
import { InputGroup } from "@dsaui/components";
import { validatePresent, validateZip } from "utils/validators";
import { States } from "utils/constants";

interface BillingFormProps {
  onSubmit: FormikConfig<PrimaryInfoFormValues>["onSubmit"];
  profile: FormValues;
  ready: boolean;
}

const validateBillingForm = (values: FormValues): FormikErrors<FormValues> => {
  const errors: FormikErrors<PrimaryInfoFormValues> = {
    firstName: validatePresent("firstName", values),
    lastName: validatePresent("lastName", values),
    primaryLine1: validatePresent("primaryLine1", values),
    primaryCity: validatePresent("primaryCity", values),
    primaryState: validatePresent("primaryState", values),
    primaryZip: validateZip("primaryZip", values, { required: true }),
  };

  return omitBy(errors, isUndefined);
};

// TODO: reconfigure this when we have a better understanding of our core 'primary'
// address fields and their purpose (not used for now)

const PrimaryInfoForm = ({
  ready,
  onSubmit,
  profile,
}: BillingFormProps): JSX.Element => {
  const { t } = useTranslation();

  const initialValues = sliceProfileValues(primaryInfoFormKeys, profile);

  return (
    <Formik<PrimaryInfoFormValues>
      validate={validateBillingForm}
      onSubmit={onSubmit}
      initialValues={initialValues}
      validateOnMount
    >
      {(fp: FormikProps<PrimaryInfoFormValues>) => (
        <form aria-label="primary form" onSubmit={fp.handleSubmit}>
          <Text as="h2" sx={{ mt: 4 }}>
            {t("forms.profile.primarySection")}
          </Text>
          <InputGroup title={t("forms.profile.primaryAddress.title")}>
            <Text as="p" variant="subtitle">
              {t("forms.profile.primaryAddress.description")}
            </Text>
            <InputGroup inline>
              <ProfileFormInput name="firstName" fp={fp} required />
              <ProfileFormInput name="lastName" fp={fp} required />
            </InputGroup>
            <ProfileFormInput name="primaryLine1" fp={fp} required />
            <ProfileFormInput name="primaryLine2" fp={fp} />
            <ProfileFormInput name="primaryCity" fp={fp} required />
            <InputGroup inline>
              <ProfileFormSelect name="primaryState" fp={fp} required>
                {States.map((choice: string) => (
                  <option key={choice} value={choice}>
                    {t(`countries.US.states.${choice}.displayName`)}
                  </option>
                ))}
              </ProfileFormSelect>
              <ProfileFormInput name="primaryZip" fp={fp} maxLength={10} required />
            </InputGroup>
          </InputGroup>
          <div>
            <SubmitButton data-testid="save-primary" fp={fp} ready={ready}>
              {t("save")}
            </SubmitButton>
          </div>
        </form>
      )}
    </Formik>
  );
};

export default PrimaryInfoForm;
